import { Box } from "@singita/components"
import { graphql } from "gatsby"
import { TEMPLATE_TYPES } from "gatsby-singita-theme/src/common/constants"
import HelmetWrapper from "gatsby-singita-theme/src/components/Helmet"
import Main from "gatsby-singita-theme/src/components/Main"
import { useLanguage } from "gatsby-singita-theme/src/context/LanguageContext"
import { useResourceSets } from "gatsby-singita-theme/src/context/ResourceSetContext"
import { useSeo } from "gatsby-singita-theme/src/hooks"
import React from "react"
import { CONTENT_TYPES, LODGE_DATA_MAP } from "../common/constants"

import HeroVideoWrapper from "../modules/HeroVideoWrapper"
import HeroWrapper from "../modules/HeroWrapper"

const LodgeActionBarWrapper = React.lazy(() =>
  import("../modules/LodgeActionBarWrapper")
)
const ActivityModalWrapper = React.lazy(() =>
  import("../components/ActivityModalWrapper")
)

const LodgeBanners = React.lazy(() => import("../modules/LodgeBanners"))
const LodgeGallery = React.lazy(() =>
  import("../components/Lodge/LodgeGallery")
)

const LodgeAlerts = React.lazy(() => import("../modules/LodgeAlerts"))
const LodgeIntro = React.lazy(() => import("../modules/LodgeIntro"))
const LodgeMainGallery = React.lazy(() => import("../modules/LodgeMainGallery"))
const LodgeActivitiesSection = React.lazy(() =>
  import("../modules/LodgeActivitiesSection")
)
const LodgeInformationSection = React.lazy(() =>
  import("../modules/LodgeInformationSection")
)
const LodgeConservation = React.lazy(() =>
  import("../modules/LodgeConservation")
)

const LodgeTemplate = props => {
  const {
    data: { lodge },
  } = props

  const patternProps = {
    color: lodge.color,
    path: lodge.pattern?.pattern,
  }

  const seoData = useSeo(lodge, LODGE_DATA_MAP, TEMPLATE_TYPES.LODGE)
  seoData.title = `${seoData.sdTitle} | ${lodge.region.headline} | Singita`

  const lang = useLanguage()
  const lodgesRS = useResourceSets("lodges", lang)

  return (
    <>
      <Main
        {...props}
        hideNewsletter={lodge.hideNewsletter}
        codes={{ lodge: lodge.code, region: lodge.region.code }}
      >
        <HelmetWrapper {...seoData} />

        {lodge.hero.__typename === CONTENT_TYPES.HERO ? (
          <HeroWrapper
            {...lodge.hero}
            image={lodge.hero.images ? lodge.hero.images[0] : null}
            patternProps={patternProps}
            colorScheme={lodge.color}
          />
        ) : (
          <HeroVideoWrapper
            {...lodge.hero}
            patternProps={patternProps}
            colorScheme={lodge.color}
          />
        )}

        <React.Suspense fallback={null}>
          <React.Suspense fallback={null}>
            <LodgeActionBarWrapper lodge={lodge} />
          </React.Suspense>
          <LodgeIntro lodge={lodge} />

          <LodgeMainGallery lodge={lodge} />

          <LodgeAlerts lodge={lodge} />

          <LodgeActivitiesSection
            activities={lodge.activities}
            title={lodge.meta?.activitiesTitle ?? ""}
            basePath={props.pageContext.basePath}
          />

          <LodgeInformationSection lodge={lodge} />

          <LodgeConservation lodge={lodge} />

          {!!lodge.galleries && (
            <Box data-testid="lodge-gallery-slider" mb={[15]}>
              <LodgeGallery
                lodge={lodge}
                patternProps={patternProps}
                buttonText={lodgesRS.gallery.buttonText}
              />
            </Box>
          )}

          <LodgeBanners lodge={lodge} />
        </React.Suspense>
      </Main>
      {(!!lodge.meta || !!lodge.activities) && (
        <React.Suspense fallback={null}>
          <ActivityModalWrapper
            title={lodge.meta ? lodge.meta.activitiesTitle : ""}
            activities={lodge.activities}
            otherActivitiesTitle={lodge.meta.otherActivitiesTitle}
          />
        </React.Suspense>
      )}
    </>
  )
}

export default LodgeTemplate

export const LODGE_QUERY = graphql`
  query lodgeQuery($slug: String!, $locale: String) {
    lodge: contentfulLodge(slug: { eq: $slug }, node_locale: { eq: $locale }) {
      id
      color
      slug
      fullTitle
      tags {
        internal {
          content
        }
      }
      hero {
        __typename
        ... on Node {
          __typename
          ... on ContentfulLayoutHero {
            ...HeroFields
          }
          ... on ContentfulLayoutHeroVideo {
            ...HeroVideoFields
          }
        }
      }
      shortDescription {
        shortDescription
      }
      region {
        headline
        country
        code
      }
      intro {
        __typename
        ...SectionLeftRightFields
      }
      conservation {
        __typename
        ...CardHeroFields
      }
      galleries {
        __typename
        ... on Node {
          ...ContentContentAssetFields
        }
      }
      highlights {
        __typename
        ... on ContentfulImage {
          tag
          ...ImageFields
          image {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: NONE
              quality: 95
              width: 1000
              height: 665
              formats: [AUTO, WEBP]
            )
          }
        }
      }
      meta {
        highlightsTitle
        activitiesTitle
        lodgeInfoTitle
        otherActivitiesTitle
      }
      activities {
        id
        tag
        contentful_id
        headline
        region {
          id
          headline
        }
        content {
          raw
        }
        images {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            quality: 90
            width: 400
            height: 260
            formats: [AUTO, WEBP]
          )
        }
      }
      lodgeInformation {
        id
        tag
        contentful_id
        headline
        content {
          raw
        }
        displayForLocale
      }
      lodgeStaff {
        headline: name
        role
        # role should be a fallback for personRole
        personRole {
          name
        }
        category
        # category should be a fallback for personCategory
        personCategory {
          name
        }
        image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            quality: 90
            width: 300
            height: 375
            formats: [AUTO, WEBP]
          )
        }
      }
      pattern {
        pattern
      }
      banners {
        __typename
        ... on ContentfulContent {
          id
          tag
          headline
          subtitle
          content {
            raw
          }
          image {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: NONE
              quality: 100
              width: 240
              formats: [AUTO, WEBP]
            )
          }
        }
      }
      ...LodgeStructuredDataFields
      siteplan {
        file {
          url
        }
      }
      brochure {
        file {
          url
        }
      }
      activitiesBrochure {
        file {
          url
        }
      }
      communityExperiences {
        file {
          url
        }
      }
      image {
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
      code
      alerts {
        ...AlertFields
      }
      hideNewsletter
    }
  }
`

export const lodgeStructuredDataFields = graphql`
  fragment LodgeStructuredDataFields on ContentfulLodge {
    id
    structuredData {
      headline
      image {
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          quality: 90
          width: 800
          formats: [AUTO, WEBP]
        )
      }
      description {
        description
      }
      altText
      meta {
        internal {
          content
        }
      }
    }
  }
`
